import { useMemo, useState } from "react";
import { useLang } from "../../../core/lang";
import LangKeys from "../../../core/locales/keys";

function EditFieldPage({ editField, setActivePage }) {
  const { t } = useLang();

  const cached = JSON.parse(localStorage.getItem("business_profile") || "null");
  const [value, setValue] = useState(editField?.value || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const config = useMemo(() => {
    switch (editField?.field) {
      case "owner_name":
        return {
          label: t(LangKeys.name),
          placeholder: t(LangKeys.enter_name),
          type: "text",
          multiline: false,
        };

      case "mobile":
        return {
          label: t(LangKeys.mobile_number),
          placeholder: t(LangKeys.enter_mobile),
          type: "tel",
          multiline: false,
        };

      case "business_name":
        return {
          label: t(LangKeys.business_name),
          placeholder: t(LangKeys.enter_business),
          type: "text",
          multiline: false,
        };

      case "address":
        return {
          label: t(LangKeys.business_address),
          placeholder: t(LangKeys.enter_address),
          type: "text",
          multiline: true,
        };

      case "state":
        return {
          label: t(LangKeys.biz_profile__state_lbl) || "State",
          placeholder: t(LangKeys.biz_profile__state_placeholder) || "e.g. Rajasthan, Gujarat...",
          type: "text",
          multiline: false,
        };

      case "city":
        return {
          label: t(LangKeys.biz_profile__city_lbl) || "City / District",
          placeholder: t(LangKeys.biz_profile__city_placeholder) || "e.g. Bikaner, Jaipur...",
          type: "text",
          multiline: false,
        };

      case "area":
        return {
          label: t(LangKeys.biz_profile__area_lbl) || "Area / Village",
          placeholder: t(LangKeys.biz_profile__area_placeholder) || "e.g. Sadhasar, Nokha...",
          type: "text",
          multiline: false,
        };

      case "pincode":
        return {
          label: t(LangKeys.biz_profile__pincode_lbl) || "Pincode",
          placeholder: t(LangKeys.biz_profile__pincode_placeholder) || "6-digit Pincode",
          type: "number",
          multiline: false,
        };

      case "store_description":
        return {
          label: t(LangKeys.biz_profile__tagline_lbl) || "Business Tagline",
          placeholder: t(LangKeys.biz_profile__tagline_placeholder) || "e.g. Best quality products guaranteed",
          type: "text",
          multiline: true,
        };

      default:
        return {
          label: t(LangKeys.edit),
          placeholder: t(LangKeys.enter_name),
          type: "text",
          multiline: false,
        };
    }
  }, [editField, t]);

  const save = async () => {
    const finalValue = value.trim();
    if (!finalValue) return;

    try {
      setSaving(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(
        "https://khata.moxpal.com/api/v2/index.php/business-profile",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            field: editField.field,
            value: finalValue,
          }),
        }
      );

      const data = await res.json();

      if (!data?.success) {
        throw new Error(data?.error?.message || t(LangKeys.update_failed));
      }

      const updated = {
        ...(cached || {}),
        [editField.field]: finalValue,
      };

      localStorage.setItem("business_profile", JSON.stringify(updated));
      setActivePage(null);
    } catch (err) {
      setError(err.message || t(LangKeys.update_failed));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => setActivePage(null)}>
          ←
        </button>
        <h2 style={styles.title}>
          {t(LangKeys.edit)} {config.label}
        </h2>
      </div>

      <div style={styles.card}>
        <div style={styles.label}>{config.label}</div>

        {config.multiline ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={config.placeholder}
            style={styles.textarea}
          />
        ) : (
          <input
            type={config.type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={config.placeholder}
            style={styles.input}
          />
        )}

        {error ? <div style={styles.error}>{error}</div> : null}

        <button
          style={{
            ...styles.saveBtn,
            ...(saving ? styles.saveBtnDisabled : {}),
          }}
          onClick={save}
          disabled={saving}
        >
          {saving ? t(LangKeys.saving) : t(LangKeys.save)}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "16px",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },

  backBtn: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    background: "#fff",
    cursor: "pointer",
    fontSize: "20px",
  },

  title: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#111827",
    margin: 0,
  },

  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "24px",
    border: "1px solid #E5E7EB",
    maxWidth: "620px",
  },

  label: {
    fontSize: "14px",
    color: "#6B7280",
    marginBottom: "8px",
  },

  input: {
    width: "100%",
    height: "50px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    padding: "0 14px",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    minHeight: "110px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    padding: "14px",
    fontSize: "15px",
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },

  error: {
    marginTop: "12px",
    fontSize: "13px",
    color: "#DC2626",
    fontWeight: "500",
  },

  saveBtn: {
    width: "100%",
    height: "48px",
    border: "none",
    borderRadius: "14px",
    background: "#2563EB",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
    marginTop: "18px",
  },

  saveBtnDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
};

export default EditFieldPage;